import React from 'react';

/**
 * Component for managing file types settings
 */
const FileTypesSection = ({
  allowedFileTypes,
  loading,
  error,
  newFileType,
  onInputChange,
  onAddFileType,
  onToggleFileType,
  onDeleteFileType,
  saveSuccess
}) => {
  // Format bytes to human readable size
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };
  
  return (
    <div className="bg-gray-900 rounded-xl shadow-xl p-6 mb-8 border border-gray-800">
      <h2 className="text-xl font-semibold mb-4 text-blue-400 text-left">Allowed File Types</h2>
      <p className="text-gray-400 mb-6 text-left">Define which file types are allowed for upload in the system</p>
      
      {/* Loading state */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 text-red-300 p-4 mb-6 rounded-r text-left" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      {/* Success message */}
      {saveSuccess && (
        <div className="bg-green-900/30 border-l-4 border-green-500 text-green-300 p-4 mb-6 rounded-r text-left" role="alert">
          <p>Changes saved successfully!</p>
        </div>
      )}
      
      {/* File types list */}
      <div className="overflow-x-auto rounded-lg ring-1 ring-gray-700 mb-8">
        <table className="min-w-full divide-y divide-gray-700">
          <thead>
            <tr>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Extension</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Display Name</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">MIME Type</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Max Size</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Status</th>
              <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {!loading && allowedFileTypes.length === 0 && (
              <tr>
                <td colSpan="6" className="p-4 text-center text-gray-400">
                  No file types found. Add your first file type using the form below.
                </td>
              </tr>
            )}
            
            {allowedFileTypes.map((fileType, index) => (
              <tr key={index} className="bg-gray-900 hover:bg-gray-800 transition-colors duration-150">
                <td className="py-3 px-4">{fileType.extension}</td>
                <td className="py-3 px-4">{fileType.displayName}</td>
                <td className="py-3 px-4">{fileType.mimeType || 'Not specified'}</td>
                <td className="py-3 px-4">{formatBytes(fileType.maxSize)}</td>
                <td className="py-3 px-4">
                  <span 
                    className={fileType.enabled ? 'text-green-300' : 'text-red-300'}
                  >
                    {fileType.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => onToggleFileType(index)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        fileType.enabled 
                          ? 'bg-yellow-900/40 text-yellow-300 hover:bg-yellow-900/60' 
                          : 'bg-green-900/40 text-green-300 hover:bg-green-900/60'
                      } transition-colors duration-200`}
                      title={fileType.enabled ? 'Disable' : 'Enable'}
                    >
                      {fileType.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => onDeleteFileType(index)}
                      className="p-1 text-red-300 hover:text-red-500 transition-colors duration-200"
                      title="Delete"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Add new file type form */}
      <div className="bg-gray-800/50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4 text-blue-400 text-left">Add New File Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-300 mb-2 text-left" htmlFor="extension">
              Extension <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              id="extension"
              name="extension"
              value={newFileType.extension}
              onChange={onInputChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder=".pdf, .doc, etc."
              dir="ltr"
            />
          </div>
          
          <div>
            <label className="block text-gray-300 mb-2 text-left" htmlFor="displayName">
              Display Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={newFileType.displayName}
              onChange={onInputChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="PDF Document"
            />
          </div>
          
          <div>
            <label className="block text-gray-300 mb-2 text-left" htmlFor="mimeType">
              MIME Type
            </label>
            <input
              type="text"
              id="mimeType"
              name="mimeType"
              value={newFileType.mimeType}
              onChange={onInputChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="application/pdf"
              dir="ltr"
            />
          </div>
          
          <div>
            <label className="block text-gray-300 mb-2 text-left" htmlFor="maxSize">
              Maximum Size (MB)
            </label>
            <input
              type="number"
              id="maxSize"
              name="maxSize"
              value={newFileType.maxSize / (1024 * 1024)}
              onChange={(e) => onInputChange({
                target: {
                  name: 'maxSize',
                  value: e.target.value * 1024 * 1024
                }
              })}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="100"
              min="1"
            />
          </div>
          
          <div className="md:col-span-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enabled"
                name="enabled"
                checked={newFileType.enabled}
                onChange={onInputChange}
                className="h-5 w-5 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-gray-300" htmlFor="enabled">
                Enabled
              </label>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <button 
            onClick={onAddFileType}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
          >
            Add File Type
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileTypesSection; 